import {
  convertDateToYYYYMMDDFormat,
  getFirstDayOfMonth,
  getToday,
  STATUSPAGE,
  SUBMIT_BUTTON,
} from '@repo/ui';
import { GetAppRegisteredStudentsPerGroupUseCaseInterface } from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearAppRegisteredStudentsPerGroupListData,
  loadAppRegisteredStudentsPerGroupListData,
} from 'presentation/config/store/reports/app-registered-students-per-group-slice';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

export const useAppRegisteredStudentsPerGroup = () => {
  // Hooks
  const dispatch = useDispatch();
  const { reportService } = useService();
  const { getLegends } = useLegends();

  // States
  const selectedSubmitBtnRef = useRef<SUBMIT_BUTTON | null>(null);
  const [buttonsLoading, setButtonsLoading] = useState(STATUSPAGE.DONE);
  const [tableLoading, setTableLoading] = useState(STATUSPAGE.DONE);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const {
    appRegisteredStudentsPerGroupListData,
    appRegisteredStudentsPerGroupFilters,
  } = useAppSelector((state) => state.appRegisteredStudentsPerGroupSlice);

  // Methods
  const requestGetAppRegisteredStudentsPerGroupPDFOrExcel = useCallback(
    async (params: GetAppRegisteredStudentsPerGroupUseCaseInterface.Params) => {
      setButtonsLoading(STATUSPAGE.LOADING);
      await reportService
        .getAppRegisteredStudentsPerGroup(params)
        .then((res: any) => {
          let blob = new Blob([res], {
            type: `application/${params?.reportType === '0' ? 'pdf' : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'}`,
          });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => notifyError(err.message))
        .finally(() => setButtonsLoading(STATUSPAGE.DONE));
    },
    [],
  );

  const requestGetAppRegisteredStudentsPerGroupJSON = useCallback(
    async (params: GetAppRegisteredStudentsPerGroupUseCaseInterface.Params) => {
      setTableLoading(STATUSPAGE.LOADING);
      setButtonsLoading(STATUSPAGE.LOADING);
      await reportService
        .getAppRegisteredStudentsPerGroup(params)
        .then((res) => dispatch(loadAppRegisteredStudentsPerGroupListData(res)))
        .catch((err) => {
          dispatch(clearAppRegisteredStudentsPerGroupListData());
          notifyError(err.message);
        })
        .finally(() => {
          setTableLoading(STATUSPAGE.DONE);
          setButtonsLoading(STATUSPAGE.DONE);
          setHasFetchedData(true);
        });
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: appRegisteredStudentsPerGroupFilters?.entity || null,
      course: appRegisteredStudentsPerGroupFilters?.course || null,
      unit: appRegisteredStudentsPerGroupFilters?.unit || null,
      semester: appRegisteredStudentsPerGroupFilters?.semester || null,
      group: appRegisteredStudentsPerGroupFilters?.group || [],
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      ends: convertDateToYYYYMMDDFormat(getToday()),
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      group: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      starts: Yup.date().required('Data inicial é obrigatório'),
      ends: Yup.date().required('Data final é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        entity: values.entity?.value,
        course: values.course?.value,
        semester: values.semester?.value,
        curricularUnit: values.unit?.value,
        groups: values.group.map((group) => group.value),
        ends: values.ends,
        starts: values.starts,
      };

      switch (selectedSubmitBtnRef.current) {
        case SUBMIT_BUTTON.PDF:
          requestGetAppRegisteredStudentsPerGroupPDFOrExcel({
            ...valuesToSubmit,
            reportType: '0',
          });
          break;
        case SUBMIT_BUTTON.EXCEL:
          requestGetAppRegisteredStudentsPerGroupPDFOrExcel({
            ...valuesToSubmit,
            reportType: '1',
          });
          break;
        case SUBMIT_BUTTON.JSON:
          requestGetAppRegisteredStudentsPerGroupJSON({
            ...valuesToSubmit,
            reportType: '2',
          });
          break;
        default:
          break;
      }
    },
  });

  return {
    // States
    validation,
    tableLoading,
    buttonsLoading,
    hasFetchedData,
    selectedSubmitBtnRef,
    appRegisteredStudentsPerGroupListData,
    // Methods
    getLegends,
    requestGetAppRegisteredStudentsPerGroupPDFOrExcel,
    requestGetAppRegisteredStudentsPerGroupJSON,
  };
};
